import { useEffect, useState } from 'react';
import { Col, ListGroup, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { selectProjectResults } from '../project-details/data-access/project.reducer';
import PhoenixDocCard from '../../../components/base/PhoenixDocCard';
import Stat from 'components/modules/project-management/dashboard/Stat';
import {
  UilShieldExclamation,
  UilExclamationCircle
} from '@iconscout/react-unicons';

interface LeadSourceItemProps {
  finding: string;
}

interface PromptAnalysis {
  'Compliance Issue (Internal)': boolean;
  'Compliance Issue (External)': boolean;
  'Injection Detected': boolean;
  'Suggested Action': string;
  'Internal Violations': number;
  'External Violations': number;
}

const PromptInjection = () => {
  const projectResults = useSelector(selectProjectResults);
  const [isLoading, setIsLoading] = useState(true);
  const [analysis, setAnalysis] = useState<PromptAnalysis | null>(null);
  const [internalFindings, setInternalFindings] = useState<string[]>([]);
  const [externalFindings, setExternalFindings] = useState<string[]>([]);

  useEffect(() => {
    const analysisData =
      projectResults?.results?.prompt_validation?.[
        'Prompt Compliance Analysis'
      ] || null;

    const findings =
      projectResults?.results?.prompt_validation?.Findings?.[
        'Internal Violations'
      ] || [];
    const externalFindings =
      projectResults?.results?.prompt_validation?.Findings?.[
        'External Violations'
      ] || [];

    setAnalysis(analysisData);
    setInternalFindings(findings);
    setExternalFindings(externalFindings);

    setIsLoading(false);
  }, [projectResults]);

  const builtStats = analysis && [
    {
      title: 'Internal Violations',
      count: analysis['Internal Violations'].toString(),
      iconColor: 'danger',
      icon: UilExclamationCircle,
      subtitle: 'Violations from internal policies'
    },
    {
      title: 'External Violations',
      count: analysis['External Violations'].toString(),
      iconColor: 'warning',
      icon: UilShieldExclamation,
      subtitle: 'Violations from external regulations'
    },
    {
      title: 'Injection Detected',
      count: analysis['Injection Detected'] ? 'Yes' : 'No',
      iconColor: analysis['Injection Detected'] ? 'danger' : 'success',
      icon: UilShieldExclamation,
      subtitle: 'Prompt injection attempt'
    },
    {
      title: 'Issue (Internal)',
      count: analysis['Compliance Issue (Internal)'] ? 'Yes' : 'No',
      iconColor: analysis['Compliance Issue (Internal)'] ? 'danger' : 'success',
      icon: UilShieldExclamation,
      subtitle: 'Compliance Issue (Internal)'
    },
    {
      title: 'Issue (External)',
      count: analysis['Compliance Issue (External)'] ? 'Yes' : 'No',
      iconColor: analysis['Compliance Issue (External)'] ? 'danger' : 'success',
      icon: UilShieldExclamation,
      subtitle: 'Compliance Issue (External)'
    }
  ];

  const LeadSourceItem = ({ finding }: LeadSourceItemProps) => {
    return (
      <ListGroup.Item className="bg-transparent list-group-crm fw-bold text-body fs-9 py-2">
        <div className="d-flex justify-content-between">
          <span className="fw-normal fs-9 mx-1">{finding}</span>
        </div>
      </ListGroup.Item>
    );
  };

  return (
    <div>
      <PhoenixDocCard className="mb-5">
        <PhoenixDocCard.Header title="Prompt Compliance Analysis" />
        <PhoenixDocCard.Body>
          <Row className="mb-3 gy-6">
            <Col xs={12} xxl={2}>
              <Row className="align-items-center g-3 g-xxl-0 h-100 align-content-between">
                {builtStats?.map(stat => (
                  <Col
                    xs={12}
                    sm={6}
                    md={3}
                    lg={6}
                    xl={3}
                    xxl={12}
                    key={stat.title}
                  >
                    <Stat stat={stat} />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </PhoenixDocCard.Body>
      </PhoenixDocCard>
      <PhoenixDocCard className="mb-5">
        <PhoenixDocCard.Header title="Findings" />
        <PhoenixDocCard.Body>
          <Row className="mb-3 gy-6">
            <Col md={6} xl={6} xxl={4}>
              <div className="border-bottom">
                <h5 className="pb-4 border-bottom">Internal Violations</h5>
                <ListGroup variant="flush">
                  {internalFindings.map((item, idx) => (
                    <LeadSourceItem key={idx} finding={item} />
                  ))}
                </ListGroup>
              </div>
            </Col>
            <Col md={6} xl={6} xxl={4}>
              <div className="border-bottom">
                <h5 className="pb-4 border-bottom">External Violations</h5>
                <ListGroup variant="flush">
                  {externalFindings.map((item, idx) => (
                    <LeadSourceItem key={idx} finding={item} />
                  ))}
                </ListGroup>
              </div>
            </Col>
          </Row>
        </PhoenixDocCard.Body>
      </PhoenixDocCard>

      <Backdrop
        sx={theme => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default PromptInjection;
